import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  Modal,
  Form,
  Input,
  Select,
  Row,
  Col,
  Button,
  Space,
  message,
  Typography,
  Spin,
  Empty,
} from "antd";
import { TeamOutlined } from "@ant-design/icons";
import traceabilityService from "../../../services/traceabilityService";
import { TTraceableInstanceGroup } from "../../../models/traceabilityModel";

const { Text } = Typography;
const { Option } = Select;
const { TextArea } = Input;

interface Props {
  visible: boolean;
  onClose: () => void;
  onSuccess: () => void;
  tokenData: any;
  companyUid: string;
  companyId: number;
  categories: any[];
  availableInstances?: any[];
  editingGroup?: TTraceableInstanceGroup | null;
}

export const RegisterTraceableGroupModal: React.FC<Props> = ({
  visible,
  onClose,
  onSuccess,
  tokenData,
  companyUid,
  companyId,
  categories,
  availableInstances = [],
  editingGroup,
}) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [loadedInstances, setLoadedInstances] = useState<any[]>([]);
  const [loadingInstances, setLoadingInstances] = useState(false);
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const fetchInstances = useCallback(
    async (searchTerm?: string) => {
      if (!companyId && !companyUid) return;
      try {
        setLoadingInstances(true);
        const res = await traceabilityService.getInstances(tokenData, {
          company_id: companyId,
          company_uid: companyUid,
          search: searchTerm?.trim() || undefined,
          limit: 100,
        });
        if (res && res.instances) {
          setLoadedInstances((prev) => {
            if (searchTerm && searchTerm.trim()) {
              const map = new Map<number, any>();
              prev.forEach((item) => map.set(item.id, item));
              res.instances.forEach((item: any) => map.set(item.id, item));
              return Array.from(map.values());
            }
            return res.instances;
          });
        }
      } catch (err) {
        console.error("Error fetching instances for group creation:", err);
      } finally {
        setLoadingInstances(false);
      }
    },
    [companyId, companyUid, tokenData]
  );

  useEffect(() => {
    if (visible) {
      if (editingGroup) {
        form.setFieldsValue({
          name: editingGroup.name,
          description: editingGroup.description,
          category_option_id: editingGroup.category_option_id,
          status: editingGroup.status,
          metadata: editingGroup.metadata || {},
        });
      } else {
        form.resetFields();
        form.setFieldsValue({
          status: "ACTIVE",
          category_option_id: categories.length > 0 ? categories[0].id : undefined,
        });
        fetchInstances();
      }
    }
  }, [visible, editingGroup, categories, fetchInstances]);

  const handleDebouncedSearch = (searchText: string) => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    searchTimeoutRef.current = setTimeout(() => {
      fetchInstances(searchText);
    }, 350);
  };

  // Combine instances from fetch and prop
  const instancePoolMap = new Map<number, any>();
  (availableInstances || []).forEach((inst) => instancePoolMap.set(inst.id, inst));
  loadedInstances.forEach((inst) => instancePoolMap.set(inst.id, inst));

  const instanceSelectOptions = Array.from(instancePoolMap.values()).map((inst) => {
    const details = [
      inst.name,
      inst.storage_elements?.name,
      inst.module_options?.string_value,
    ]
      .filter(Boolean)
      .join(" • ");

    return {
      value: inst.id,
      label: `${inst.identifier}${details ? ` (${details})` : ""}`,
      searchText: `${inst.identifier} ${inst.name || ""} ${inst.storage_elements?.name || ""} ${inst.module_options?.string_value || ""}`.toLowerCase(),
    };
  });

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setLoading(true);

      const groupPayload = {
        company_id: companyId,
        name: values.name,
        description: values.description || null,
        category_option_id: values.category_option_id || null,
        status: values.status || "ACTIVE",
        metadata: values.metadata || {},
      };

      if (editingGroup) {
        const res = await traceabilityService.updateGroup(tokenData, {
          company_uid: companyUid,
          group: {
            ...groupPayload,
            id: editingGroup.id,
          },
        });
        if (res.error) throw new Error(res.message);
        message.success("Grupo trazable actualizado con éxito");
      } else {
        const res = await traceabilityService.createGroup(tokenData, {
          company_uid: companyUid,
          group: groupPayload,
          initial_instance_ids: values.initial_instance_ids || [],
        });
        if (res.error) throw new Error(res.message);
        message.success("Grupo trazable creado con éxito");
      }

      onSuccess();
      onClose();
    } catch (err: any) {
      message.error(err.message || "Error al procesar el grupo");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      open={visible}
      title={
        <Space align="center">
          <TeamOutlined style={{ color: "#722ed1", fontSize: 20 }} />
          <span>{editingGroup ? `Editar Grupo: ${editingGroup.name}` : "Nuevo Grupo / Hato / Lote Trazable"}</span>
        </Space>
      }
      width={650}
      onCancel={onClose}
      footer={[
        <Button key="back" onClick={onClose}>
          Cancelar
        </Button>,
        <Button key="submit" type="primary" loading={loading} onClick={handleSubmit}>
          {editingGroup ? "Guardar Cambios" : "Crear Grupo"}
        </Button>,
      ]}
      destroyOnClose
    >
      <Form form={form} layout="vertical">
        <Row gutter={16}>
          <Col xs={24} md={16}>
            <Form.Item
              name="name"
              label="Nombre del Grupo"
              rules={[{ required: true, message: "Ingrese el nombre del grupo" }]}
            >
              <Input placeholder="Ej. Hato Engorde Lote 5, Flota Sede Norte, Batch 2026-A" />
            </Form.Item>
          </Col>

          <Col xs={24} md={8}>
            <Form.Item name="status" label="Estado" rules={[{ required: true }]}>
              <Select>
                <Option value="ACTIVE">Activo</Option>
                <Option value="CLOSED">Cerrado</Option>
                <Option value="ARCHIVED">Archivado</Option>
              </Select>
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col xs={24} md={12}>
            <Form.Item name="category_option_id" label="Categoría">
              <Select placeholder="Seleccionar categoría">
                {categories.map((cat) => (
                  <Option key={cat.id} value={cat.id}>
                    {cat.string_value}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>

          {!editingGroup && (
            <Col xs={24} md={12}>
              <Form.Item
                name="initial_instance_ids"
                label="Agregar Elementos Iniciales (Opcional)"
                help="Seleccione los elementos para asignar a este grupo"
              >
                <Select
                  mode="multiple"
                  showSearch
                  placeholder="Buscar por identificador, nombre..."
                  allowClear
                  maxTagCount="responsive"
                  onSearch={handleDebouncedSearch}
                  filterOption={(input, option) => {
                    if (!input) return true;
                    const term = input.toLowerCase();
                    const labelText = (option?.label as string) || "";
                    const searchContent = (option as any)?.searchText || "";
                    return labelText.toLowerCase().includes(term) || searchContent.includes(term);
                  }}
                  options={instanceSelectOptions}
                  loading={loadingInstances}
                  notFoundContent={
                    loadingInstances ? (
                      <div style={{ textAlign: "center", padding: "8px 0" }}>
                        <Spin size="small" /> Cargando elementos...
                      </div>
                    ) : (
                      <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No hay elementos disponibles" />
                    )
                  }
                />
              </Form.Item>
            </Col>
          )}
        </Row>

        <Row gutter={16}>
          <Col span={24}>
            <Form.Item name="description" label="Descripción / Notas">
              <TextArea rows={3} placeholder="Detalles del objetivo del grupo, lote, características comunes..." />
            </Form.Item>
          </Col>
        </Row>
      </Form>
    </Modal>
  );
};

export default RegisterTraceableGroupModal;